// Fenster "Leads zusammenführen" (L21): zeigt, welcher Lead bleibt, was zu ihm wandert (Kontakte, Aktivitäten, Aufgaben …), welche leeren
// Felder gefüllt werden und was beachtet werden muss. Die Richtung lässt sich tauschen. Genutzt von der Dublettenansicht und vom
// Lead-Menü (`components/lead/dublettenMenue.tsx`). Das Zusammenführen gilt im CRM; in Close muss Dilsar es von Hand nachziehen.
import { useEffect, useState } from "react";
import { dublettenApi, type DubletteLead, type ZusammenfuehrenErgebnis, type ZusammenfuehrenVorschau } from "../api/dubletten";
import { StatusPille } from "../components/Bausteine";
import { Dialog } from "../components/Dialog";
import { fehlerInfo } from "../components/Fehler";
import { Icon } from "../components/Icon";
import { berlinDatum, datumText } from "../werkzeuge/zeit";
import { TX } from "./texte";

export interface LeadRef {
  id: string;
  name: string;
}

export function tagText(iso: string | null | undefined): string {
  return iso ? datumText(berlinDatum(iso)) : "";
}

export function wertText(w: unknown): string {
  if (w === null || w === undefined || w === "") return "–";
  if (typeof w === "string") return w.length > 70 ? `${w.slice(0, 70)} …` : w;
  if (Array.isArray(w)) {
    return w
      .map((x) => (x && typeof x === "object" ? Object.values(x as Record<string, unknown>).filter((v) => typeof v === "string" && v).join(", ") : String(x)))
      .join(" | ")
      .slice(0, 90);
  }
  return String(w);
}

function LeadKarte({ titel, lead, aktiv }: { titel: string; lead: DubletteLead; aktiv: boolean }) {
  return (
    <div className={`dub-karte${aktiv ? " dub-karte--ziel" : ""}`}>
      <div className="dub-karte__titel">{titel}</div>
      <div className="dub-karte__name" title={lead.name ?? undefined}>
        {lead.name}
      </div>
      <div className="dub-karte__zeile">
        {lead.status_label ? <StatusPille label={lead.status_label} klein schlicht /> : null}
        {lead.hat_kundenakte ? (
          <span className="marke marke--leise" title={TX.kundenakteTitel}>
            {TX.kundenakte}
          </span>
        ) : null}
      </div>
      <div className="leise dub-karte__daten">
        Angelegt {tagText(lead.date_created) || "–"} · {lead.kontakte} {lead.kontakte === 1 ? "Kontakt" : "Kontakte"} · {lead.aktivitaeten} {lead.aktivitaeten === 1 ? "Aktivität" : "Aktivitäten"}
      </div>
      {lead.telefon || lead.email || lead.ort ? <div className="leise dub-karte__daten">{[lead.telefon, lead.email, lead.ort].filter(Boolean).join(" · ")}</div> : null}
    </div>
  );
}

export function ZusammenfuehrenDialog({ offen, ziel, quelle, onSchliessen, onFertig, tauschbar = true }: {
  offen: boolean;
  ziel: LeadRef;
  quelle: LeadRef;
  onSchliessen: () => void;
  onFertig: (ergebnis: ZusammenfuehrenErgebnis) => void;
  tauschbar?: boolean;
}) {
  const [umgekehrt, setUmgekehrt] = useState(false);
  const [vorschau, setVorschau] = useState<ZusammenfuehrenVorschau | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const zielId = umgekehrt ? quelle.id : ziel.id;
  const quelleId = umgekehrt ? ziel.id : quelle.id;

  useEffect(() => {
    if (!offen) return;
    setUmgekehrt(false);
  }, [offen, ziel.id, quelle.id]);

  useEffect(() => {
    if (!offen) return;
    let aktuell = true;
    setVorschau(null);
    setFehler(null);
    dublettenApi
      .vorschau(zielId, quelleId)
      .then((v) => aktuell && setVorschau(v))
      .catch((e) => aktuell && setFehler(fehlerInfo(e).text));
    return () => {
      aktuell = false;
    };
  }, [offen, zielId, quelleId]);

  const ausfuehren = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      onFertig(await dublettenApi.zusammenfuehren(zielId, quelleId));
    } catch (e) {
      setFehler(fehlerInfo(e).text);
    } finally {
      setLaeuft(false);
    }
  };

  const w = vorschau?.wandert;
  return (
    <Dialog
      titel={TX.zfTitel}
      offen={offen}
      onSchliessen={onSchliessen}
      breite={640}
      fuss={
        <>
          <button type="button" className="knopf" onClick={onSchliessen}>
            Abbrechen
          </button>
          <button type="button" className="knopf knopf--primaer" disabled={!vorschau || vorschau.blockiert || laeuft} onClick={() => void ausfuehren()}>
            {laeuft ? "Wird zusammengeführt …" : TX.zfAusfuehren}
          </button>
        </>
      }
    >
      {fehler ? <div className="hinweis hinweis--fehler">{fehler}</div> : null}
      {!vorschau && !fehler ? <div className="leise">Wird geladen …</div> : null}
      {vorschau ? (
        <div className="dub-zf">
          <div className="dub-zf__leads">
            <LeadKarte titel={TX.zfZiel} lead={vorschau.ziel} aktiv />
            <div className="dub-zf__mitte">
              <Icon name="pfeil-links" groesse={18} />
              {tauschbar ? (
                <button type="button" className="knopf knopf--klein knopf--leise" onClick={() => setUmgekehrt((u) => !u)} title={TX.zfTauschen}>
                  <Icon name="sortieren" groesse={14} /> {TX.zfTauschen}
                </button>
              ) : null}
            </div>
            <LeadKarte titel={TX.zfQuelle} lead={vorschau.quelle} aktiv={false} />
          </div>

          <div className="dub-zf__abschnitt">
            <div className="dub-zf__ueber">{TX.zfWandert}</div>
            <ul className="dub-zf__zahlen">
              <li><strong>{w?.kontakte}</strong> {w?.kontakte === 1 ? "Kontakt" : "Kontakte"}</li>
              <li><strong>{w?.aktivitaeten}</strong> {w?.aktivitaeten === 1 ? "Aktivität" : "Aktivitäten"}</li>
              <li><strong>{w?.aufgaben}</strong> {w?.aufgaben === 1 ? "Aufgabe" : "Aufgaben"}</li>
              <li><strong>{w?.opportunities}</strong> {w?.opportunities === 1 ? "Opportunity" : "Opportunities"}</li>
              {w && w.dateien ? <li><strong>{w.dateien}</strong> {w.dateien === 1 ? "Datei" : "Dateien"}</li> : null}
            </ul>
            <div className="leise">Dazu Anrufe, SMS, E-Mails, Entwürfe und das Log.</div>
          </div>

          {vorschau.felder.length ? (
            <div className="dub-zf__abschnitt">
              <div className="dub-zf__ueber">{TX.zfFelder}</div>
              <div className="leise">{TX.zfFelderText}</div>
              <table className="tabelle tabelle--einfach dub-zf__felder">
                <thead>
                  <tr>
                    <th>Feld</th>
                    <th>Bleibender Lead</th>
                    <th>Anderer Lead</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {vorschau.felder.map((f) => (
                    <tr key={f.feld + String(f.uebernommen)}>
                      <td>{f.feld}</td>
                      <td>{f.feld === "Angelegt am" ? tagText(String(f.ziel)) : wertText(f.ziel)}</td>
                      <td>{f.feld === "Angelegt am" ? tagText(String(f.quelle)) : wertText(f.quelle)}</td>
                      <td className="leise">{f.uebernommen ? "wird übernommen" : "bleibt"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {vorschau.blockiert ? <div className="hinweis hinweis--fehler">{TX.zfGesperrt}</div> : null}
          {vorschau.hinweise.map((h) => (
            <div key={h} className="hinweis hinweis--info">
              {h}
            </div>
          ))}
        </div>
      ) : null}
    </Dialog>
  );
}
